import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export const useUnreadMessages = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deferred, setDeferred] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Defer initial fetch to not block first paint
  useEffect(() => {
    const timer = setTimeout(() => setDeferred(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUnreadCount(0);
        return;
      }

      // Get all public messages (recipient_id is null)
      const { data: publicMessages, error: publicError } = await supabase
        .from('chat_messages')
        .select('id')
        .is('recipient_id', null);

      if (publicError) throw publicError;

      // Get read status for current user
      const { data: readStatus, error: readError } = await supabase
        .from('message_read_status')
        .select('message_id')
        .eq('user_id', user.id);

      if (readError) throw readError;

      const readMessageIds = new Set(readStatus?.map(r => r.message_id) || []);
      
      // Count messages that are not sent by current user and not read
      const { data: unreadMessages, error: unreadError } = await supabase
        .from('chat_messages')
        .select('id')
        .is('recipient_id', null)
        .neq('sender_id', user.id);

      if (unreadError) throw unreadError;

      const unread = (unreadMessages || []).filter(msg => !readMessageIds.has(msg.id));
      setUnreadCount(unread.length);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!deferred) return;
    
    fetchUnreadCount();

    // Subscribe to realtime updates for chat_messages
    const messagesChannel = supabase
      .channel('unread-messages-count')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
        },
        () => {
          fetchUnreadCount();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_read_status',
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    channelRef.current = messagesChannel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [deferred, fetchUnreadCount]);

  return {
    unreadCount,
    loading,
    refetch: fetchUnreadCount,
  };
};
